/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import React, { useState, useRef, useEffect } from "react";
import {
	MapPin,
	Phone,
	Mail,
	Clock3,
	Send,
	Loader2,
	Building2,
	Globe,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register GSAP plugin
gsap.registerPlugin(ScrollTrigger);

// ============================================================================
// DATA
// ============================================================================
const pageData = {
	title: "Contact Us",
	image: "/images/compnay2.png",
	description:
		"Use the form for RFQs, technical discussions, qualification requests, or early-stage project conversations.",
	meta: ["Request a Quote", "Technical Consultation", "After-Sales Support"],

	details: [
		{
			id: "address",
			titleKey: "contact.details.manufacturingHeadOffice",
			value: {
				company: "Positron Engineering Corporation",
				addressLine1: "Plot No. 42, Machhe Industrial Area &",
				addressLine2: "Plot No. 148 & 149, Sy. No. 585 & 590",
				addressLine3: "Machhe, Belagavi - 590014",
				state: "Karnataka",
				country: "India",
				map: "google.com",
			},
			icon: MapPin,
		},

		{
			id: "phone",
			titleKey: "contact.details.phone",
			value: ["+91 831-2411834"],
			icon: Phone,
		},
		{
			id: "email",
			titleKey: "contact.details.email",
			value: ["info@positron-he.com"],
			icon: Mail,
		},
		{
			id: "hours",
			titleKey: "contact.details.businessHours",
			valueKey: "contact.details.businessHoursValue",
			value: "Monday to Saturday, 9:00 AM to 6:30 PM",
			icon: Clock3,
		},
	],

	internationalOffices: [
		{
			titleKey: "contact.office.uaeOffice",
			value: {
				company: "Positron International FZCO",
				addressLine1: "Building A1, Dubai Digital Park",
				addressLine2: "Dubai Silicon Oasis",
				city: "Dubai",
				country: "United Arab Emirates",
				email: "myv@positron-he.com",
				phone: "+971 54 275 5275",
				map: "google.com",
			},
		},
	],

	cta: {
		title: "Talk to our engineers about your next thermal requirement.",
		description:
			"Early technical alignment usually leads to stronger equipment selection, clearer documentation, and fewer downstream compromises.",
	},
};

// ============================================================================
// TYPES
// ============================================================================
interface FormData {
	name: string;
	email: string;
	phone: string;
	company: string;
	subject: string;
	message: string;
}

interface FormErrors {
	name?: string;
	email?: string;
	phone?: string;
	company?: string;
	subject?: string;
	message?: string;
}

type LocationType = "india" | "uae";

// ============================================================================
// MAP CONFIGURATION
// ============================================================================
const mapLocations = {
	india: {
		title: "Positron Engineering Corporation - Head Office",
		embedUrl:
			"https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3839.3015798519245!2d74.47035067538049!3d15.788044284851363!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bbf658eaaaaaaab%3A0x52cde407f2d67f41!2sPositron%20Engineering%20Corporation!5e0!3m2!1sen!2sin!4v1776142605410!5m2!1sen!2sin",
	},
	uae: {
		title: "Positron International FZCO - Dubai Office",
		embedUrl:
			"https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3612.503915042601!2d55.375163975614946!3d25.11864897776156!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f651c2438dc53%3A0xe6abcc1ea38a17f5!2sDigital%20Park%20D1%20Building!5e0!3m2!1sen!2sin!4v1776142667177!5m2!1sen!2sin",
	},
};

// ============================================================================
// COMPONENTS
// ============================================================================

// Animated Meta Badges
const MetaBadges: React.FC<{ badges: string[] }> = ({ badges }) => {
	return (
		<div className="flex flex-wrap gap-3 mt-4">
			{badges.map((badge, index) => (
				<motion.span
					key={badge}
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
					className="px-4 py-1.5 text-xs font-medium tracking-wide uppercase bg-gray-100 border border-gray-200 rounded-full text-gray-600">
					{badge}
				</motion.span>
			))}
		</div>
	);
};

// Contact Detail Item - Address fields stacked vertically
const ContactDetailItem: React.FC<{
	item: (typeof pageData.details)[number];
	index: number;
}> = ({ item, index }) => {
	const { t } = useTranslation();
	const Icon = item.icon;
	const isAddress = item.id === "address";
	const valueKey = "valueKey" in item ? item.valueKey : undefined;
	const values =
		valueKey ? [t(valueKey)]
		: Array.isArray(item.value) ? item.value
		: [item.value as string];
	const addressObj = isAddress ? (item.value as any) : null;

	return (
		<motion.div
			initial={{ opacity: 0, x: -20 }}
			animate={{ opacity: 1, x: 0 }}
			transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
			whileHover={{ scale: 1.01, transition: { duration: 0.2 } }}
			className="group flex gap-5 p-5 rounded-xl bg-white border border-gray-200 shadow-sm hover:shadow-md hover:border-primary/30 transition-all duration-300">
			<div className="flex-shrink-0 w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform duration-300">
				<Icon size={20} />
			</div>
			<div className="flex-1">
				<h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-3">
					{t(item.titleKey)}
				</h3>
				{isAddress && addressObj ?
					<div className="text-gray-700 space-y-1">
						<p className="font-semibold text-gray-800">{addressObj.company}</p>
						<p>{addressObj.addressLine1}</p>
						<p>{addressObj.addressLine2}</p>
						<p>{addressObj.addressLine3}</p>

						<p>
							{addressObj.state}, {addressObj.country}
						</p>
					</div>
				:	values.map((val, i) => (
						<p
							key={i}
							className="text-gray-800 font-medium">
							{val}
						</p>
					))
				}
			</div>
		</motion.div>
	);
};

const InternationalOfficeCard: React.FC<{
	office: (typeof pageData.internationalOffices)[0];
	index: number;
}> = ({ office, index }) => {
	const { t } = useTranslation();
	const ref = useRef<HTMLDivElement>(null);
	const data = office.value;

	useEffect(() => {
		gsap.fromTo(
			ref.current,
			{ opacity: 0, y: 40 },
			{
				opacity: 1,
				y: 0,
				duration: 0.8,
				ease: "power3.out",
				scrollTrigger: { trigger: ref.current, start: "top 90%" },
			},
		);
	}, []);

	return (
		<motion.div
			ref={ref}
			initial={{ opacity: 0, y: 40 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true }}
			transition={{ duration: 0.6, delay: index * 0.1 }}
			className="w-full p-5 sm:p-8 rounded-3xl bg-white border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300">
			{/* Header */}
			<div className="flex items-center gap-3 sm:gap-4 mb-5 sm:mb-6">
				<div className="p-2.5 sm:p-3 rounded-xl bg-primary/10 text-primary shrink-0">
					<Building2 size={24} />
				</div>
				<h3 className="text-xl sm:text-2xl font-bold text-gray-800">
					{t(office.titleKey)}
				</h3>
			</div>

			{/* ✅ 2x2 Grid FIX */}
			<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
				{/* Company */}
				<div className="p-4 rounded-xl border bg-gray-50 hover:bg-white transition">
					<p className="text-xs text-gray-500 mb-1">
						{t("contact.office.company")}
					</p>
					<p className="font-semibold text-gray-800">{data.company}</p>
				</div>

				{/* Address */}
				<div className="p-4 rounded-xl border bg-gray-50 hover:bg-white transition">
					<p className="text-xs text-gray-500 mb-1">
						{t("contact.office.address")}
					</p>
					<p className="text-sm text-gray-700 leading-relaxed">
						{data.addressLine1} <br />
						{data.addressLine2} <br />
						{data.city}, {data.country}
					</p>
				</div>

				{/* Email */}
				<div className="p-4 rounded-xl border bg-gray-50 hover:bg-white transition">
					<p className="text-xs text-gray-500 mb-1">
						{t("contact.office.email")}
					</p>
					<p className="flex items-center gap-2 text-sm text-gray-700">
						<Mail
							size={14}
							className="text-primary"
						/>
						{data.email}
					</p>
				</div>

				{/* Phone */}
				<div className="p-4 rounded-xl border bg-gray-50 hover:bg-white transition">
					<p className="text-xs text-gray-500 mb-1">
						{t("contact.office.phone")}
					</p>
					<p className="flex items-center gap-2 text-sm text-gray-700">
						<Phone
							size={14}
							className="text-primary"
						/>
						{data.phone}
					</p>
				</div>
			</div>
		</motion.div>
	);
};
// Map Section with Toggle
// const MapSection: React.FC = () => {
// 	const [activeLocation, setActiveLocation] = useState<LocationType>("india");
// 	const [isTransitioning, setIsTransitioning] = useState(false);

// 	const handleToggle = (location: LocationType) => {
// 		if (location === activeLocation) return;
// 		setIsTransitioning(true);
// 		setTimeout(() => {
// 			setActiveLocation(location);
// 			setTimeout(() => setIsTransitioning(false), 100);
// 		}, 200);
// 	};

// 	return (
// 		<div className="mt-16">
// 			<motion.div
// 				initial={{ opacity: 0, y: 30 }}
// 				whileInView={{ opacity: 1, y: 0 }}
// 				viewport={{ once: true }}
// 				transition={{ duration: 0.6 }}
// 				className="rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-sm">
// 				{/* Toggle Buttons */}
// 				<div className="flex border-b border-gray-200">
// 					<button
// 						onClick={() => handleToggle("india")}
// 						className={`flex-1 py-4 text-center font-medium transition-all duration-300 ${
// 							activeLocation === "india" ?
// 								"bg-primary/5 text-primary border-b-2 border-primary"
// 							:	"text-gray-500 hover:text-gray-700 hover:bg-gray-50"
// 						}`}>
// 						<span className="mr-0 lg:mr-2">
// 							<img
// 								src="\flags\arabic.png"
// 								alt="in"
// 								className="w-5 h-5 "
// 							/>
// 						</span>
// 						India Office
// 					</button>
// 					<button
// 						onClick={() => handleToggle("uae")}
// 						className={`flex-1 py-4 text-center font-medium transition-all duration-300 ${
// 							activeLocation === "uae" ?
// 								"bg-primary/5 text-primary border-b-2 border-primary"
// 							:	"text-gray-500 hover:text-gray-700 hover:bg-gray-50"
// 						}`}>
// 						<span className="mr-0 lg:mr-2">
// 							<img
// 								src="\flags\arabic.png"
// 								alt="ae"
// 								className="w-5 h-5 "
// 							/>
// 						</span>
// 						UAE Office
// 					</button>
// 				</div>

// 				{/* Map Container with Animation */}
// 				<div className="relative h-80 md:h-96 overflow-hidden">
// 					<AnimatePresence mode="wait">
// 						<motion.div
// 							key={activeLocation}
// 							initial={{ opacity: 0, scale: 0.98 }}
// 							animate={{
// 								opacity: isTransitioning ? 0 : 1,
// 								scale: isTransitioning ? 0.98 : 1,
// 							}}
// 							exit={{ opacity: 0, scale: 0.98 }}
// 							transition={{ duration: 0.3 }}
// 							className="absolute inset-0">
// 							<iframe
// 								title={mapLocations[activeLocation].title}
// 								src={mapLocations[activeLocation].embedUrl}
// 								className="w-full h-full"
// 								style={{ border: 0 }}
// 								allowFullScreen
// 								loading="lazy"
// 								referrerPolicy="no-referrer-when-downgrade"
// 							/>
// 						</motion.div>
// 					</AnimatePresence>

// 					{/* Overlay Gradient for Premium Feel */}
// 					<div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-gray-100/20 via-transparent to-transparent" />
// 				</div>
// 			</motion.div>
// 		</div>
// 	);
// };

const MapSection: React.FC = () => {
	const { t } = useTranslation();
	const [activeLocation, setActiveLocation] = useState<LocationType>("india");

	const handleToggle = (location: LocationType) => {
		if (location === activeLocation) return;
		setActiveLocation(location);
	};

	return (
		<div className="mt-16">
			<motion.div
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true }}
				transition={{ duration: 0.6 }}
				className="rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-sm">
				{/* Toggle Buttons */}
				<div className="flex border-b border-gray-200">
					{/* INDIA */}
					<button
						onClick={() => handleToggle("india")}
						className={`flex-1 flex items-center justify-center gap-2 py-4 font-medium transition-all duration-300 ${
							activeLocation === "india" ?
								"bg-primary/5 text-primary border-b-2 border-primary"
							:	"text-gray-500 hover:text-gray-700 hover:bg-gray-50"
						}`}>
						<img
							src="\flags\india.png"
							alt="India"
							className="w-5 h-5 object-contain"
						/>
						{t("contact.office.indiaOffice")}
					</button>

					{/* UAE */}
					<button
						onClick={() => handleToggle("uae")}
						className={`flex-1 flex items-center justify-center gap-2 py-4 font-medium transition-all duration-300 ${
							activeLocation === "uae" ?
								"bg-primary/5 text-primary border-b-2 border-primary"
							:	"text-gray-500 hover:text-gray-700 hover:bg-gray-50"
						}`}>
						<img
							src="\flags\arabic.png"
							alt="UAE"
							className="w-5 h-5 object-contain"
						/>
						{t("contact.office.uaeOffice")}
					</button>
				</div>

				{/* Map Section */}
				<div className="relative h-80 md:h-96 overflow-hidden">
					<AnimatePresence mode="wait">
						<motion.div
							key={activeLocation}
							initial={{ opacity: 0, scale: 0.97 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.97 }}
							transition={{ duration: 0.3 }}
							className="absolute inset-0">
							<iframe
								title={mapLocations[activeLocation].title}
								src={mapLocations[activeLocation].embedUrl}
								className="w-full h-full"
								style={{ border: 0 }}
								loading="lazy"
								referrerPolicy="no-referrer-when-downgrade"
							/>
						</motion.div>
					</AnimatePresence>

					{/* Premium Gradient Overlay */}
					<div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-gray-100/20 via-transparent to-transparent" />
				</div>
			</motion.div>
		</div>
	);
};

// Contact Form with Validation
const ContactForm: React.FC = () => {
	const { t } = useTranslation();
	const [formData, setFormData] = useState<FormData>({
		name: "",
		email: "",
		phone: "",
		company: "",
		subject: "",
		message: "",
	});
	const [errors, setErrors] = useState<FormErrors>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitSuccess, setSubmitSuccess] = useState(false);
	const formRef = useRef<HTMLDivElement>(null);

	// GSAP form entrance animation
	useEffect(() => {
		gsap.fromTo(
			formRef.current,
			{ opacity: 0, x: 50 },
			{ opacity: 1, x: 0, duration: 0.8, ease: "power2.out" },
		);
	}, []);

	// Returns a translation key (or "" when valid) so messages follow the
	// active language and update live if the user switches mid-form.
	const validateField = (name: string, value: string): string => {
		switch (name) {
			case "name":
				return !value.trim() ? "contact.form.errors.nameRequired" : "";
			case "email":
				if (!value.trim()) return "contact.form.errors.emailRequired";
				return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ?
						"contact.form.errors.emailInvalid"
					:	"";
			case "phone":
				return !value.trim() ? "contact.form.errors.phoneRequired" : "";
			case "company":
				return !value.trim() ? "contact.form.errors.companyRequired" : "";
			case "subject":
				return !value ? "contact.form.errors.subjectRequired" : "";
			case "message":
				return !value.trim() ? "contact.form.errors.messageRequired" : "";
			default:
				return "";
		}
	};

	const handleChange = (
		e: React.ChangeEvent<
			HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
		>,
	) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
		const error = validateField(name, value);
		setErrors((prev) => ({ ...prev, [name]: error }));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Validate all fields
		const newErrors: FormErrors = {};
		Object.keys(formData).forEach((key) => {
			const error = validateField(key, formData[key as keyof FormData]);
			if (error) newErrors[key as keyof FormErrors] = error;
		});

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}

		setIsSubmitting(true);
		// Simulate API call
		await new Promise((resolve) => setTimeout(resolve, 1500));
		setIsSubmitting(false);
		setSubmitSuccess(true);
		setFormData({
			name: "",
			email: "",
			phone: "",
			company: "",
			subject: "",
			message: "",
		});
		setTimeout(() => setSubmitSuccess(false), 3000);
	};

	const inputClasses = (hasError: boolean) => `
    w-full px-4 py-3 rounded-xl bg-gray-50 border text-gray-800 placeholder-gray-400
    focus:outline-none focus:ring-2 transition-all duration-300
    ${hasError ? "border-red-400 focus:ring-red-300" : "border-gray-200 focus:border-primary focus:ring-primary/30"}
  `;

	return (
		<motion.div
			ref={formRef}
			initial={{ opacity: 0, x: 50 }}
			animate={{ opacity: 1, x: 0 }}
			transition={{ duration: 0.6 }}
			className="p-6 md:p-8 rounded-2xl bg-white border border-gray-200 shadow-sm">
			<h3 className="text-2xl font-bold text-gray-800 mb-6">
				{t("contact.form.title")}
			</h3>

			<form
				onSubmit={handleSubmit}
				className="space-y-5">
				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.1 }}>
					<input
						type="text"
						name="name"
						placeholder={t("contact.form.namePlaceholder")}
						aria-label={t("contact.form.namePlaceholder")}
						value={formData.name}
						onChange={handleChange}
						className={inputClasses(!!errors.name)}
					/>
					{errors.name && (
						<p className="text-red-500 text-xs mt-1">{t(errors.name)}</p>
					)}
				</motion.div>

				<div className="grid gap-4 md:grid-cols-2">
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.15 }}>
						<input
							type="email"
							name="email"
							placeholder={t("contact.form.emailPlaceholder")}
							aria-label={t("contact.form.emailPlaceholder")}
							value={formData.email}
							onChange={handleChange}
							className={inputClasses(!!errors.email)}
						/>
						{errors.email && (
							<p className="text-red-500 text-xs mt-1">{t(errors.email)}</p>
						)}
					</motion.div>

					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.2 }}>
						<input
							type="tel"
							name="phone"
							placeholder={t("contact.form.phonePlaceholder")}
							aria-label={t("contact.form.phonePlaceholder")}
							value={formData.phone}
							onChange={handleChange}
							className={inputClasses(!!errors.phone)}
						/>
						{errors.phone && (
							<p className="text-red-500 text-xs mt-1">{t(errors.phone)}</p>
						)}
					</motion.div>
				</div>
				<div className="grid gap-4 md:grid-cols-2">
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.25 }}>
						<input
							type="text"
							name="company"
							placeholder={t("contact.form.companyPlaceholder")}
							aria-label={t("contact.form.companyPlaceholder")}
							value={formData.company}
							onChange={handleChange}
							className={inputClasses(!!errors.company)}
						/>
						{errors.company && (
							<p className="text-red-500 text-xs mt-1">{t(errors.company)}</p>
						)}
					</motion.div>

					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.3 }}>
						<select
							name="subject"
							value={formData.subject}
							onChange={handleChange}
							aria-label={t("contact.form.subjectPlaceholder")}
							className={inputClasses(!!errors.subject)}>
							<option
								value=""
								className="text-gray-500">
								{t("contact.form.subjectPlaceholder")}
							</option>
							<option value="RFQ">{t("contact.form.subjectRfq")}</option>
							<option value="Technical Discussion">
								{t("contact.form.subjectTechnical")}
							</option>
							<option value="Project Support">
								{t("contact.form.subjectProject")}
							</option>
						</select>
						{errors.subject && (
							<p className="text-red-500 text-xs mt-1">{t(errors.subject)}</p>
						)}
					</motion.div>
				</div>

				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.35 }}>
					<textarea
						name="message"
						placeholder={t("contact.form.messagePlaceholder")}
						aria-label={t("contact.form.messagePlaceholder")}
						rows={4}
						value={formData.message}
						onChange={handleChange}
						className={inputClasses(!!errors.message)}
					/>
					{errors.message && (
						<p className="text-red-500 text-xs mt-1">{t(errors.message)}</p>
					)}
				</motion.div>

				<motion.button
					type="submit"
					disabled={isSubmitting}
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.4 }}
					whileHover={{ scale: 1.02 }}
					whileTap={{ scale: 0.98 }}
					className="w-full py-4 rounded-xl bg-gradient-to-r from-primary to-primary/80 text-white font-semibold flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-lg hover:shadow-primary/30 disabled:opacity-70 disabled:cursor-not-allowed">
					{isSubmitting ?
						<>
							<Loader2
								size={20}
								className="animate-spin"
							/>
							{t("contact.form.sending")}
						</>
					: submitSuccess ?
						<>
							<Send size={20} />
							{t("contact.form.sentSuccess")}
						</>
					:	<>
							<Send size={20} />
							{t("contact.form.send")}
						</>
					}
				</motion.button>
			</form>
		</motion.div>
	);
};

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================
const ContactPage: React.FC = () => {
	const { t } = useTranslation();
	const headerRef = useRef<HTMLDivElement>(null);
	const mainRef = useRef<HTMLDivElement>(null);
	const ctaRef = useRef<HTMLDivElement>(null);

	// GSAP animations on mount
	useEffect(() => {
		const ctx = gsap.context(() => {
			// Header stagger animation
			gsap.fromTo(
				".header-line",
				{ opacity: 0, y: 30 },
				{ opacity: 1, y: 0, duration: 0.6, stagger: 0.15, ease: "power2.out" },
			);

			// CTA scroll-triggered animation
			gsap.fromTo(
				ctaRef.current,
				{ opacity: 0, scale: 0.95 },
				{
					opacity: 1,
					scale: 1,
					duration: 0.6,
					scrollTrigger: { trigger: ctaRef.current, start: "top 85%" },
				},
			);
		});

		return () => ctx.revert();
	}, []);

	return (
		<div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
			{/* Subtle industrial pattern overlay - light version */}
			<div
				className="fixed inset-0 opacity-[0.02] pointer-events-none"
				style={{
					backgroundImage:
						'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23333" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
				}}
			/>

			<div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
				{/* Main 2-Column Grid */}
				<div
					ref={mainRef}
					className="grid lg:grid-cols-[1fr_1.6fr] gap-8 mt-12">
					{/* Left Side - Contact Details */}
					<div className="space-y-4">
						{pageData.details.map((item, idx) => (
							<ContactDetailItem
								key={idx}
								item={item}
								index={idx}
							/>
						))}
					</div>

					{/* Right Side - Contact Form */}

					<div className="h-full">
						<div
							className="w-full h-full min-h-[300px] rounded-xl overflow-hidden bg-cover bg-center"
							style={{
								backgroundImage: `url(${pageData.image})`,
							}}
						/>
					</div>
				</div>

				<div className="relative left-1/2 w-screen -translate-x-1/2 mt-16">
					<div className="max-w-7xl mx-auto px-6">
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.5 }}
							className="flex items-center gap-3 mb-8">
							<Globe
								size={28}
								className="text-primary"
							/>
							<h2 className="text-2xl font-bold text-gray-800">
								{t("contact.globalPresence")}
							</h2>
						</motion.div>

						{/* 🔥 Split Layout */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
							{/* LEFT → UAE Office */}
							<div className="h-full">
								<InternationalOfficeCard
									office={pageData.internationalOffices[0]} // UAE
									index={0}
								/>
							</div>

							{/* RIGHT → Company Image */}
							<ContactForm />
						</div>
					</div>
				</div>

				{/* Map Section */}
				<MapSection />

				{/* CTA Section */}
				<motion.div
					ref={ctaRef}
					initial={{ opacity: 0, scale: 0.95 }}
					whileInView={{ opacity: 1, scale: 1 }}
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
					className="mt-16 p-8 md:p-10 rounded-2xl bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border border-primary/20 text-center">
					<h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
						{t("contact.cta.title")}
					</h3>
					<p className="text-gray-600 max-w-2xl mx-auto">
						{t("contact.cta.description")}
					</p>
				</motion.div>
			</div>
		</div>
	);
};

export default ContactPage;
